import api from "./api";

/** One column of the attendance report: a session, as a two-line header. */
export type ReportSessionCol = {
  id: string;
  start_at: string;
  /** "Luni" */
  day: string;
  /** "5 Oct" */
  date: string;
  type: string;
  title: string;
};

export type Mark = "x" | "o" | "m" | "-";

export type ReportRow = {
  athlete_id: string;
  nume: string;
  prenume: string;
  full_name: string;
  birth_year: number | null;
  phone: string | null;
  phone_of: "sportiv" | "părinte" | null;
  marks: Record<string, Mark>;
  present: number;
  absent: number;
  excused: number;
  unmarked: number;
  rate: number;
};

export type AttendanceReport = {
  team: { id: string; name: string; age_group: string | null; sport: string | null };
  club: { id: string; name: string };
  period: { year: number; month: number; label: string };
  sessions: ReportSessionCol[];
  rows: ReportRow[];
  totals: { athletes: number; sessions: number; present: number; absent: number; excused: number };
  legend: string;
};

export type ReportTeam = {
  id: string;
  name: string;
  age_group: string | null;
  club_id: string;
  club_name: string;
  sport_id: string | null;
  sport_name: string | null;
};

export type ReportPeriod = { year: number; month: number; label: string };

/**
 * What the picker is built from.
 *
 * The months come back from the server because they are the months that
 * actually have sessions — offering an empty one is offering a dead end.
 */
export async function getReportTeams(): Promise<{
  teams: ReportTeam[];
  sports: { id: string; name: string }[];
  months: ReportPeriod[];
}> {
  const { data } = await api.get("/reports/attendance/teams");
  return { teams: data.teams || [], sports: data.sports || [], months: data.months || [] };
}

export async function getAttendanceReport(input: {
  teamId: string;
  year: number;
  month: number;
}): Promise<AttendanceReport> {
  const { data } = await api.get<AttendanceReport>("/reports/attendance", {
    params: { team_id: input.teamId, year: input.year, month: input.month },
  });
  return data;
}

/**
 * Download the CSV.
 *
 * Fetched through the API client rather than linked: the endpoint needs the
 * bearer token, which a plain `<a href>` would not send.
 */
export async function downloadAttendanceReport(input: {
  teamId: string;
  year: number;
  month: number;
  fileName?: string;
}): Promise<void> {
  const { data } = await api.get("/reports/attendance/export", {
    params: { team_id: input.teamId, year: input.year, month: input.month },
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = input.fileName || `prezenta-${input.year}-${String(input.month).padStart(2, "0")}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Send it by email, with the CSV attached. Defaults to the caller's address. */
export async function emailAttendanceReport(input: {
  teamId: string;
  year: number;
  month: number;
  to?: string;
}): Promise<{ to: string }> {
  const { data } = await api.post<{ to: string }>("/reports/attendance/email", {
    team_id: input.teamId,
    year: input.year,
    month: input.month,
    ...(input.to ? { to: input.to } : {}),
  });
  return data;
}
